import {
  AppBar as MuiAppBar,
  Avatar,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  PopoverOrigin,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  MenuItemLink,
  useCreatePath,
  useGetIdentity,
  useGetList,
  useGetResourceLabel,
  useLogout,
  useResourceDefinitions,
  useTheme,
  UserMenuContext,
} from "react-admin";
import GlobalSearchBox from "./GlobalSearchBox";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeftIcon } from "@mui/x-date-pickers";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";
import { currentUser, grants, restrictAccessToDrivingSchoolIds } from "../backoffice.access_control";
import { useAutovioContext } from "../hooks/useAutovioContext";
import CopyToClipboardIcon from "@mui/icons-material/ContentCopy";
import Divider from "@mui/material/Divider";
import { DrivingSchoolLogo } from "./DrivingSchoolLogo";
import { useMenu } from "../hooks/useMenu";
import { safeLocalStorage } from "../utils/safeLocalStorage";
import { studentsProvider } from "../providers/studentsProvider";
import { useQueryClient } from "react-query";
import { useRerender } from "../hooks/useRerender.js";
import { currentVersion } from "../../version";
import { drivingSchoolResources, vehicleResources } from "./DefaultList";
import { runSafelyWithServerClient } from "../api/server.api";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { BackofficeSideBar } from "./BackofficeSideBar";
import { menuItemStyle as userMenuItemSx, menuPaperStyle as userMenuPaperSx } from "./backofficeTheme";

export function BackofficeAppBar() {
  const [autovioContext, setAutovioContext] = useAutovioContext();
  const { pathname } = useLocation();
  const isMobile = useMediaQuery("(max-width: 899px)");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const isCalendarEventDispatcher = pathname.startsWith("/calendar-event/");
  const hasDrivingSchoolContext = !!autovioContext.drivingSchoolId && !isCalendarEventDispatcher;
  const hideInUserMenu = ["drivingSchools", ...drivingSchoolResources, "vehicles", ...vehicleResources];
  if (restrictAccessToDrivingSchoolIds?.length === 1) {
    hideInUserMenu.push("bi");
  }
  // Make sure the drivingSchoolId in the autovioContext is always in sync with the URL ...
  useEffect(() => {
    const match = /\/drivingSchools\/([^/]+)/.exec(pathname);
    if (match) {
      const drivingSchoolId = match[1];
      if (drivingSchoolId !== autovioContext.drivingSchoolId) {
        setAutovioContext({ drivingSchoolId });
      }
    }
  }, [autovioContext.drivingSchoolId, pathname]);
  return (
    <>
      <MuiAppBar
        elevation={0}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          height: isMobile ? "64px" : "88px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--backoffice-background-default)",
          backgroundImage: "none",
          flexDirection: "row",
          paddingLeft: isMobile ? 0 : "40px",
          paddingRight: isMobile ? 0 : "40px",
          ...(isMobile
            ? {
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                width: "auto",
                maxWidth: "none",
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "hidden",
                zIndex: 1200,
              }
            : {}),
        }}
      >
        {isMobile ? (
          <MobileAppBarContent
            exclude={hideInUserMenu}
            showMenuButton={hasDrivingSchoolContext}
            onOpenMenu={() => setMobileMenuOpen(true)}
            onOpenSearch={() => setMobileSearchOpen(true)}
          />
        ) : (
          <>
            <DrivingSchoolMenu />
            <div id="global-search-box">
              <GlobalSearchBox />
            </div>
            <UserMenu exclude={hideInUserMenu} />
          </>
        )}
      </MuiAppBar>
      <MobileSearchDialog open={mobileSearchOpen} onClose={() => setMobileSearchOpen(false)} />
      {hasDrivingSchoolContext && (
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          PaperProps={{
            sx: { width: 280, maxHeight: "100dvh", overflowY: "auto", backgroundColor: "background.default" },
          }}
        >
          <BackofficeSideBar variant="mobileDrawer" onNavigate={() => setMobileMenuOpen(false)} />
        </Drawer>
      )}
    </>
  );
}

function MobileAppBarContent(props: {
  exclude: string[];
  showMenuButton: boolean;
  onOpenMenu: () => void;
  onOpenSearch: () => void;
}) {
  const { exclude, showMenuButton, onOpenMenu, onOpenSearch } = props;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "48px minmax(0, 1fr) 96px",
        alignItems: "center",
        flex: "1 1 0",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
        px: "8px",
      }}
    >
      {showMenuButton ? (
        <IconButton sx={{ justifySelf: "start" }} aria-label="Hauptmenü öffnen" onClick={onOpenMenu}>
          <MenuIcon sx={{ color: "text.primary" }} />
        </IconButton>
      ) : (
        <Box />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <DrivingSchoolMenu variant="compact" />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifySelf: "end", minWidth: 0 }}>
        <IconButton aria-label="Suche öffnen" onClick={onOpenSearch}>
          <SearchIcon sx={{ color: "text.primary" }} />
        </IconButton>
        <UserMenu exclude={exclude} variant="avatarOnly" />
      </Box>
    </Box>
  );
}

function MobileSearchDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "100vw",
          maxWidth: "100vw",
          minWidth: 0,
          borderRadius: 0,
          overflowX: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 48px",
          alignItems: "center",
          gap: 1,
          width: "100vw",
          maxWidth: "100vw",
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "hidden",
          p: "10px 8px 10px 16px",
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.default",
        }}
      >
        <Box id="mobile-global-search-box" sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
          <GlobalSearchBox autoFocus onSelect={onClose} variant="mobile" />
        </Box>
        <IconButton sx={{ flexShrink: 0 }} aria-label="Suche schließen" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
    </Dialog>
  );
}

const userMenuAnchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};

const userMenuTransformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};

const UserMenu = (props: { exclude: string[]; variant?: "default" | "avatarOnly" }) => {
  const { variant = "default" } = props;
  const isAvatarOnly = variant === "avatarOnly";
  const { anchorEl, openMenu, closeMenu } = useMenu();
  const { identity } = useGetIdentity();
  const resources = useResourceDefinitions();
  const getResourceLabel = useGetResourceLabel();
  const createPath = useCreatePath();
  const userMenuContext = useMemo(() => ({ onClose: closeMenu }), [closeMenu]);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  return (
    <div id="user-menu">
      <List component="nav" sx={{ background: "transparent" }}>
        <ListItemButton
          id="UserMenuButton"
          sx={{ borderRadius: "8px", ml: isAvatarOnly ? 0 : "12px", minWidth: isAvatarOnly ? 48 : undefined, p: 0.5 }}
          aria-haspopup="listbox"
          aria-controls="UserMenu"
          aria-label={isAvatarOnly ? "Benutzermenü öffnen" : undefined}
          onClick={openMenu}
        >
          <Avatar
            sx={{
              ml: isAvatarOnly ? 0 : "4px",
              width: isAvatarOnly ? "32px" : "40px",
              height: isAvatarOnly ? "32px" : "40px",
              mr: isAvatarOnly ? 0 : "12px",
            }}
            src={identity?.avatar ?? ""}
            alt=""
          />
          {!isAvatarOnly && (
            <>
              <Typography sx={{ color: "text.primary", fontWeight: "600", lineHeight: 1.2, fontSize: "15px" }}>
                {identity?.fullName}
              </Typography>
              <ArrowLeftIcon
                sx={{
                  transform: anchorEl ? "rotate(90deg)" : "rotate(270deg)",
                  fontSize: 20,
                  color: "text.primary",
                  marginLeft: 0.5,
                }}
              />
            </>
          )}
        </ListItemButton>
      </List>
      <UserMenuContext.Provider value={userMenuContext}>
        <Menu
          id="UserMenu"
          disableScrollLock
          anchorEl={anchorEl}
          anchorOrigin={userMenuAnchorOrigin}
          transformOrigin={userMenuTransformOrigin}
          open={!!anchorEl}
          onClose={closeMenu}
          slotProps={{
            paper: {
              sx: userMenuPaperSx,
            },
          }}
        >
          {Object.keys(resources)
            .filter((name) => resources[name].hasList)
            .filter((name) => !props.exclude.includes(name))
            .filter((name) => name !== "theoryQuestions" || grants.has("admin"))
            .map((name) => (
              <React.Fragment key={name}>
                <MenuItemLink
                  to={createPath({ resource: name, type: "list" })}
                  primaryText={getResourceLabel(name, 2)}
                  sx={userMenuItemSx}
                  onClick={closeMenu}
                />
                {name === "theoryQuestions" ? <Divider sx={{ my: 0.5, borderColor: "divider" }} /> : null}
              </React.Fragment>
            ))}
          {grants.has("viewCustomizationFlagsUsage") && <CustomizationFlagsUsageMenuItem closeMenu={closeMenu} />}
          {grants.has("copyIdTokenToClipboard") && <CopyIdTokenMenuItem closeMenu={closeMenu} />}
          {grants.has("webinar") && (
            <>
              <ToggleWebinarModeMenuItem />
              <Divider sx={{ my: 0.5, borderColor: "divider" }} />
            </>
          )}
          {grants.has("previewStudentDetails2") && <ToggleStudentDetails2MenuItem />}
          <ThemeMenuItem />
          <Divider sx={{ my: 0.5, borderColor: "divider" }} />
          <ResetPasswordMenuItem closeMenu={closeMenu} onOpenDialog={() => setResetPasswordDialogOpen(true)} />
          <LogoutMenuItem />
          <CurrentVersionMenuItem />
        </Menu>
      </UserMenuContext.Provider>
      <ResetPasswordDialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} />
    </div>
  );
};

function ThemeMenuItem() {
  const [theme, setTheme] = useTheme();
  const darkMode = theme === "dark";

  return (
    <MenuItem
      sx={userMenuItemSx}
      role="menuitemcheckbox"
      aria-checked={darkMode}
      onClick={() => setTheme(darkMode ? "light" : "dark")}
    >
      <ListItemIcon>
        <DarkModeOutlinedIcon />
      </ListItemIcon>
      <ListItemText primary="Dark Mode" />
      <Switch
        checked={darkMode}
        size="small"
        sx={{ ml: 2, pointerEvents: "none" }}
        inputProps={{ "aria-hidden": true, tabIndex: -1 }}
      />
    </MenuItem>
  );
}

function _renderSchoolAddress(drivingSchool: DrivingSchool) {
  const { postalAddress: address } = drivingSchool;
  if (!address) return "";
  return `${address.street}, ${address.postalCode} ${address.city}`;
}

const DrivingSchoolMenu = (props: { variant?: "default" | "compact" }) => {
  const { variant = "default" } = props;
  const isCompact = variant === "compact";
  const [autovioContext, setAutovioContext] = useAutovioContext();
  const { data: drivingSchools = [] } = useGetList<DrivingSchool>("drivingSchools", {
    pagination: { page: 1, perPage: 999 },
    sort: { field: "name", order: "ASC" },
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterValue, setFilterValue] = useState<string>("");
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const open = !!anchorEl;
  const openMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const handleMenuItemClick = (option?: DrivingSchool) => {
    if (!option) {
      setAutovioContext({ drivingSchoolId: undefined });
      navigate("/");
    } else {
      if (option.id !== autovioContext.drivingSchoolId) {
        setAutovioContext({ drivingSchoolId: option.id });
      }
      const pathPrefix = `/drivingSchools/${option.id}`;
      const match = /\/drivingSchools\/([^/]+)\/([^/]+)/.exec(pathname);
      if (match) {
        navigate(`${pathPrefix}/${match[2]}${hash}`);
      } else {
        (() => {
          for (const resource of drivingSchoolResources) {
            if (pathname.startsWith(`/${resource}`)) {
              navigate(`${pathPrefix}/${resource}`);
              return;
            }
          }
          if (pathname.startsWith("/cars")) {
            navigate(`${pathPrefix}/fleet`);
            return;
          }
          for (const resource of vehicleResources) {
            if (pathname.startsWith(`/${resource}`)) {
              navigate(`${pathPrefix}/fleet#${resource}`);
              return;
            }
          }
          navigate(`/drivingSchools/${option.id}`);
        })();
      }
    }
    setFilterValue("");
    closeMenu();
  };
  const currentDrivingSchool = useMemo(
    () => drivingSchools.find(({ id }) => id === autovioContext.drivingSchoolId),
    [drivingSchools, autovioContext.drivingSchoolId],
  );
  const filteredDrivingSchools = useMemo(
    () =>
      drivingSchools.filter(
        ({ name, postalAddress }) =>
          name?.toLowerCase().includes(filterValue) || postalAddress?.city?.toLowerCase()?.includes(filterValue),
      ),
    [drivingSchools, filterValue],
  );

  const hasInactiveSchools = drivingSchools.some((it) => !it.isActive);

  return (
    <div id="driving-school-menu" style={isCompact ? { maxWidth: "100%", overflow: "hidden" } : undefined}>
      <List component="nav" sx={{ background: "transparent", maxWidth: isCompact ? "100%" : undefined }}>
        <ListItemButton
          id="DrivingSchoolMenuButton"
          sx={{
            borderRadius: "8px",
            ml: isCompact ? 0 : "-16px",
            minWidth: 0,
            maxWidth: isCompact ? "100%" : undefined,
            overflow: isCompact ? "hidden" : undefined,
            p: isCompact ? "6px 8px" : undefined,
          }}
          aria-haspopup="listbox"
          aria-controls="DrivingSchoolMenu"
          aria-expanded={open ? "true" : undefined}
          onClick={openMenu}
        >
          {!isCompact &&
            (currentDrivingSchool ? (
              <DrivingSchoolLogo drivingSchool={currentDrivingSchool} size={40} />
            ) : (
              <img src="/AUTOVIO_logo_green.svg" width="44" height="44" alt="" />
            ))}
          <Box sx={{ minWidth: 0, maxWidth: isCompact ? "100%" : undefined }}>
            <Box sx={{ ml: isCompact ? 0 : "10px", display: "flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}>
              <ListItemText
                sx={{
                  color: "text.primary",
                  flexGrow: isCompact ? 1 : 0,
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: 600,
                    fontSize: 15,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: isCompact ? "center" : "left",
                  },
                }}
                primary={currentDrivingSchool ? currentDrivingSchool.name : "Alle Fahrschulen"}
              />
              {restrictAccessToDrivingSchoolIds?.length !== 1 ? (
                <ArrowLeftIcon
                  sx={{
                    transform: anchorEl ? "rotate(90deg)" : "rotate(270deg)",
                    flexShrink: 0,
                    fontSize: 20,
                    color: "text.primary",
                  }}
                />
              ) : null}
            </Box>
            {!isCompact && (
              <Typography sx={{ ml: "10px", color: "text.secondary", marginTop: -0.7, fontSize: 14 }}>
                {currentDrivingSchool ? _renderSchoolAddress(currentDrivingSchool) : "Ganz Deutschland"}
              </Typography>
            )}
          </Box>
        </ListItemButton>
      </List>
      {restrictAccessToDrivingSchoolIds?.length !== 1 ? (
        <Menu
          id="DrivingSchoolMenu"
          anchorEl={anchorEl}
          open={open}
          onClose={closeMenu}
          MenuListProps={{
            "aria-labelledby": "DrivingSchoolMenuButton",
            role: "listbox",
            sx: {
              padding: "8px",
              width: "calc(100% - 16px) !important",
            },
          }}
          slotProps={{
            paper: {
              sx: {
                ...userMenuPaperSx,
                minWidth: isCompact ? 0 : 380,
                width: isCompact ? "min(380px, calc(100vw - 32px))" : undefined,
              },
            },
          }}
          autoFocus={false}
          disableAutoFocusItem
        >
          <div style={{ padding: "8px" }}>
            <TextField
              sx={{ borderRadius: 8, width: "100%" }}
              size="small"
              placeholder="Suche"
              value={filterValue}
              onChange={(event) => setFilterValue(event.target.value)}
              onKeyDown={(event) => {
                // Only pass through arrow down and arrow up key events to the <Menu> component ...
                if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
                  event.stopPropagation();
                }
                // If the user presses Enter and there is only one driving school in the search result, select it ...
                if (event.key === "Enter" && filteredDrivingSchools.length === 1) {
                  handleMenuItemClick(filteredDrivingSchools[0]);
                }
              }}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end" sx={{ marginLeft: -0.5, paddingRight: 1 }}>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {drivingSchools.length > 1 && !filterValue ? (
            <div>
              <MenuItem sx={userMenuItemSx} selected={!currentDrivingSchool?.id} onClick={() => handleMenuItemClick()}>
                <img src="/AUTOVIO_logo_green.svg" width="32" height="32" alt="" />
                <Box sx={{ ml: "10px" }}>
                  <Typography sx={{ fontSize: 14 }}>Alle Fahrschulen</Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Ganz Deutschland</Typography>
                </Box>
              </MenuItem>
              <Divider sx={{ my: 0.5, borderColor: "divider" }} />
            </div>
          ) : null}

          {(hasInactiveSchools ? filteredDrivingSchools.filter((it) => it.isActive) : filteredDrivingSchools).map(
            (it) => renderDrivingSchoolMenuItem(it, currentDrivingSchool, filteredDrivingSchools, handleMenuItemClick),
          )}
          {hasInactiveSchools && (
            <>
              <Divider sx={{ "&.MuiDivider-root": { my: 0, "::before": { width: "58px" } } }} textAlign="left">
                <Typography variant="body2" color="text.secondary">
                  Inaktiv
                </Typography>
              </Divider>
              {filteredDrivingSchools
                .filter((it) => !it.isActive)
                .map((it) =>
                  renderDrivingSchoolMenuItem(it, currentDrivingSchool, filteredDrivingSchools, handleMenuItemClick),
                )}
            </>
          )}
        </Menu>
      ) : null}
    </div>
  );
};

function renderDrivingSchoolMenuItem(
  drivingSchool: DrivingSchool,
  currentDrivingSchool: DrivingSchool | undefined,
  filteredDrivingSchools: DrivingSchool[],
  handleMenuItemClick: (option?: DrivingSchool) => void,
) {
  const isInactive = !drivingSchool.isActive;
  const isSelected =
    drivingSchool.id === currentDrivingSchool?.id ||
    (filteredDrivingSchools.length === 1 && filteredDrivingSchools[0].id === drivingSchool.id);
  return (
    <MenuItem
      key={drivingSchool.id}
      sx={userMenuItemSx}
      selected={isSelected}
      onClick={() => handleMenuItemClick(drivingSchool)}
    >
      <Box sx={{ flexShrink: 0, opacity: isInactive ? 0.5 : 1, filter: isInactive ? "grayscale(100%)" : "none" }}>
        <DrivingSchoolLogo drivingSchool={drivingSchool} size={32} />
      </Box>
      <Box sx={{ ml: "10px", minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            color: isInactive ? "text.disabled" : "text.primary",
            fontSize: 14,
            fontWeight: isSelected ? "bold" : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {drivingSchool.name}
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {_renderSchoolAddress(drivingSchool)}
        </Typography>
      </Box>
    </MenuItem>
  );
}

function CustomizationFlagsUsageMenuItem({ closeMenu }: { closeMenu: () => void }) {
  const navigate = useNavigate();
  return (
    <MenuItem
      sx={userMenuItemSx}
      onClick={() => {
        closeMenu();
        navigate("/customization-flags-usage");
      }}
    >
      <ListItemText>Customization Flags Usage</ListItemText>
    </MenuItem>
  );
}

function CopyIdTokenMenuItem({ closeMenu }: { closeMenu: () => void }) {
  return (
    <MenuItem
      sx={userMenuItemSx}
      onClick={async () => {
        const idToken = await currentUser!.getIdToken();
        let isClipboardAllowed = true;
        if (navigator.permissions?.query) {
          try {
            const permissionStatus = await navigator.permissions.query({ name: "clipboard-write" as any });
            isClipboardAllowed = permissionStatus.state === "granted" || permissionStatus.state === "prompt";
          } catch {
            // Some browsers (e.g. Safari) throw because clipboard-write is unsupported.
            isClipboardAllowed = true;
          }
        }
        if (isClipboardAllowed) {
          await navigator.clipboard.writeText(idToken);
          showSuccessToast("ID-Token in die Zwischenablage kopiert.");
        } else {
          showErrorToast("Kopieren in die Zwischenablage ist nicht erlaubt.");
        }
        closeMenu();
      }}
    >
      <ListItemIcon>
        <CopyToClipboardIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Kopiere ID-Token in die Zwischenablage</ListItemText>
    </MenuItem>
  );
}

function ToggleWebinarModeMenuItem() {
  const [checked, setChecked] = useState(safeLocalStorage.getItem("webinarMode") === "on");
  const queryClient = useQueryClient();
  const rerender = useRerender();

  const handleToggle = async () => {
    setChecked((prev) => !prev);
    if (checked) {
      safeLocalStorage.removeItem("webinarMode");
      studentsProvider.setWebinarMode(false);
    } else {
      safeLocalStorage.setItem("webinarMode", "on");
      studentsProvider.setWebinarMode(true);
    }
    await queryClient.invalidateQueries("students");
    rerender();
  };

  return (
    <MenuItem sx={userMenuItemSx} onClick={handleToggle}>
      <ListItemIcon>
        <Checkbox checked={checked} size="small" sx={{ p: 0 }} />
      </ListItemIcon>
      <ListItemText primary="Webinar Mode" />
    </MenuItem>
  );
}

function ToggleStudentDetails2MenuItem() {
  const [checked, setChecked] = useState(safeLocalStorage.getItem("previewStudentDetails2") === "on");
  const rerender = useRerender();

  const handleToggle = () => {
    setChecked((prev) => !prev);
    if (checked) {
      safeLocalStorage.removeItem("previewStudentDetails2");
    } else {
      safeLocalStorage.setItem("previewStudentDetails2", "on");
    }
    rerender();
  };

  return (
    <MenuItem sx={userMenuItemSx} onClick={handleToggle}>
      <ListItemIcon>
        <Checkbox checked={checked} size="small" sx={{ p: 0 }} />
      </ListItemIcon>
      <ListItemText primary="Vorschau: StudentDetails2" />
    </MenuItem>
  );
}

function ResetPasswordMenuItem(props: { closeMenu: () => void; onOpenDialog: () => void }) {
  const { closeMenu, onOpenDialog } = props;

  function handleClick() {
    closeMenu();
    setTimeout(() => {
      onOpenDialog();
    }, 0);
  }

  return (
    <MenuItem sx={userMenuItemSx} onClick={handleClick}>
      <ListItemText>Passwort zurücksetzen...</ListItemText>
    </MenuItem>
  );
}

function ResetPasswordDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const [loading, setLoading] = useState(false);

  function handleCancel() {
    onClose();
    showSuccessToast("Erfolgreich abgebrochen");
  }

  async function handleConfirm() {
    setLoading(true);
    try {
      const email = currentUser?.email?.trim();
      if (!email) {
        showErrorToast("Für deinen Account ist keine E-Mail-Adresse hinterlegt.");
        setLoading(false);
        return;
      }
      await runSafelyWithServerClient((serverClient) => serverClient.sendPasswordResetEmail({ email }), {
        successMessage:
          "Wir haben dir eine E-Mail mit weiteren Anweisungen geschickt. Bitte prüfe dein E-Mail Postfach",
        errorMessage: "Fehler beim Versenden der E-Mail zum Zurücksetzen des Passworts.",
      });
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>Passwort zurücksetzen</DialogTitle>
      <DialogContent sx={{ pt: 2, minWidth: 400 }}>
        <Typography>Wenn du dein Passwort zurücksetzt, erhälst du eine E-Mail mit weiteren Anweisungen</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <LoadingButton onClick={handleCancel} variant="outlined">
          Abbrechen
        </LoadingButton>
        <LoadingButton onClick={handleConfirm} variant="contained" loading={loading}>
          Passwort zurücksetzen
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function LogoutMenuItem() {
  const logout = useLogout();
  return (
    <MenuItem sx={userMenuItemSx} onClick={logout}>
      <ListItemText sx={{ color: "error.main" }}>Abmelden</ListItemText>
    </MenuItem>
  );
}

function CurrentVersionMenuItem() {
  return (
    <MenuItem sx={userMenuItemSx} disabled>
      <ListItemText sx={{ color: "text.secondary" }}>{`Backoffice Version: ${currentVersion}`}</ListItemText>
    </MenuItem>
  );
}
